import React, { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { RedProfessionista } from '../../types/red'
import { redApi } from '../../services/api'
import { downloadBlobResponse } from '../../lib/downloadBlob'
import NuovoProfessionistaModal from '../../components/red/NuovoProfessionistaModal'

const TIPO_LABEL: Record<string, string> = {
  tecnico: 'Tecnico / Ufficio tecnico',
  notaio: 'Notaio',
  impresa: 'Impresa',
}

const RedRubricaProfessionisti: React.FC = () => {
  const [professionisti, setProfessionisti] = useState<RedProfessionista[]>([])
  const [loading, setLoading] = useState(true)
  const [filtroTipo, setFiltroTipo] = useState('')
  const [ricerca, setRicerca] = useState('')
  const [showNuovo, setShowNuovo] = useState(false)
  const [editing, setEditing] = useState<RedProfessionista | null>(null)
  const [importing, setImporting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const load = () => {
    redApi.professionisti.list(filtroTipo || undefined).then(res => {
      setProfessionisti(res.data)
      setLoading(false)
    })
  }

  useEffect(load, [filtroTipo])

  const handleExport = async () => {
    try {
      const res = await redApi.professionisti.exportXlsx()
      downloadBlobResponse(res, 'professionisti.xlsx')
    } catch {
      toast.error('Errore durante l\'esportazione')
    }
  }

  const handleImportFile = async (file: File) => {
    setImporting(true)
    try {
      const res = await redApi.professionisti.importXlsx(file)
      const { creati, aggiornati, saltati } = res.data
      toast.success(`Import completato: ${creati} creati, ${aggiornati} aggiornati${saltati ? `, ${saltati} righe saltate` : ''}`)
      load()
    } catch {
      toast.error('Errore durante l\'importazione')
    } finally {
      setImporting(false)
    }
  }

  const handleDelete = async (p: RedProfessionista) => {
    if (!confirm(`Eliminare "${p.nome}" dalla rubrica? Verranno rimosse anche le associazioni agli immobili.`)) return
    try {
      await redApi.professionisti.delete(p.id)
      toast.success('Professionista eliminato')
      load()
    } catch {
      toast.error('Errore eliminazione')
    }
  }

  const testo = ricerca.trim().toLowerCase()
  const filtrati = professionisti.filter(p => {
    if (!testo) return true
    return [p.nome, p.comune, p.email, p.ruolo, p.professione, p.referente]
      .filter(Boolean)
      .some(v => v!.toLowerCase().includes(testo))
  })

  if (loading) return <div className="p-8 text-gray-500">Caricamento...</div>

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rubrica Professionisti</h1>
          <p className="text-sm text-gray-500 mt-1">{filtrati.length} professionisti</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            className="input text-sm"
            placeholder="Cerca nome, città, email..."
            value={ricerca}
            onChange={e => setRicerca(e.target.value)}
          />
          <select className="select text-sm" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
            <option value="">Tutti i tipi</option>
            {Object.entries(TIPO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button onClick={handleExport} className="btn-secondary text-sm">Esporta</button>
          <button onClick={() => fileInputRef.current?.click()} disabled={importing} className="btn-secondary text-sm">
            {importing ? 'Importazione...' : 'Importa'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={e => {
              const file = e.target.files?.[0]
              if (file) handleImportFile(file)
              e.target.value = ''
            }}
          />
          <button onClick={() => setShowNuovo(true)} className="btn-primary">+ Nuovo</button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm bg-white rounded-lg border border-gray-200">
          <thead>
            <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
              <th className="py-2 px-3">Nome</th>
              <th className="py-2 px-3">Ruolo</th>
              <th className="py-2 px-3">Indirizzo</th>
              <th className="py-2 px-3">Referente</th>
              <th className="py-2 px-3">Contatti</th>
              <th className="py-2 px-3">IBAN</th>
              <th className="py-2 px-3">Note</th>
              <th className="py-2 px-3 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtrati.map(p => (
              <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2 px-3">
                  <div className="font-medium text-gray-900">{p.professione ? `${p.professione} ` : ''}{p.nome}</div>
                  <div className="text-xs text-gray-400">{TIPO_LABEL[p.tipo] || p.tipo}</div>
                </td>
                <td className="py-2 px-3 text-gray-600">{p.ruolo}</td>
                <td className="py-2 px-3 text-gray-600">
                  {p.indirizzo && <div>{p.indirizzo}</div>}
                  <div>{p.comune} {p.provincia && `(${p.provincia})`}</div>
                </td>
                <td className="py-2 px-3 text-gray-600">{p.referente}</td>
                <td className="py-2 px-3 text-gray-600">
                  {p.email && <div><a href={`mailto:${p.email}`} className="text-blue-600 hover:underline">{p.email}</a></div>}
                  {p.telefono && <div>{p.telefono}</div>}
                  {p.cellulare && <div className="text-gray-400">{p.cellulare}</div>}
                </td>
                <td className="py-2 px-3 text-gray-500 text-xs">
                  {p.iban && <div>{p.iban}</div>}
                  {p.iban2 && <div>{p.iban2}</div>}
                </td>
                <td className="py-2 px-3 text-gray-500 text-xs max-w-xs">
                  {p.note && <div>{p.note}</div>}
                  {p.varie_interne && <div className="text-amber-600">{p.varie_interne}</div>}
                </td>
                <td className="py-2 px-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(p)} className="text-blue-600 text-xs hover:underline mr-3">Modifica</button>
                  <button onClick={() => handleDelete(p)} className="text-red-500 text-xs hover:underline">Elimina</button>
                </td>
              </tr>
            ))}
            {filtrati.length === 0 && (
              <tr><td colSpan={8} className="py-10 text-center text-gray-400">Nessun professionista trovato.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showNuovo && (
        <NuovoProfessionistaModal
          onClose={() => setShowNuovo(false)}
          onCreated={() => load()}
        />
      )}

      {editing && (
        <NuovoProfessionistaModal
          professionistaEsistente={editing}
          onClose={() => setEditing(null)}
          onCreated={() => load()}
        />
      )}
    </div>
  )
}

export default RedRubricaProfessionisti
